/**
 * Station accent colors for Manufacturing reporting (Time tracking summary cards and Daily task timeline).
 * Order matches `buildManufacturingReportingStations()` in manufacturing-reporting-stations.ts.
 */
export const MANUFACTURING_REPORTING_STATION_COLORS = [
  {
    key: "receiving",
    label: "Receiving",
    shortLabel: "Receiving",
    accent: "bg-lime-600",
    block: "bg-lime-600 border-lime-700",
    activeBlock: "border-l-2 border-lime-700 bg-gradient-to-r from-lime-600 to-transparent",
    laborTokens: ["receiving"],
  },
  {
    key: "transfer_pack",
    label: "Transfer Pack",
    shortLabel: "Transfer Pack",
    accent: "bg-orange-500",
    block: "bg-orange-500 border-orange-600",
    activeBlock: "border-l-2 border-orange-600 bg-gradient-to-r from-orange-500 to-transparent",
    laborTokens: ["transfer-pack", "transfer pack"],
  },
  {
    key: "transfers",
    label: "Transfers",
    shortLabel: "Transfers",
    accent: "bg-indigo-500",
    block: "bg-indigo-500 border-indigo-600",
    activeBlock: "border-l-2 border-indigo-600 bg-gradient-to-r from-indigo-500 to-transparent",
    laborTokens: ["transfers"],
  },
  {
    key: "misc",
    label: "Misc",
    shortLabel: "Misc",
    accent: "bg-rose-500",
    block: "bg-rose-500 border-rose-600",
    activeBlock: "border-l-2 border-rose-600 bg-gradient-to-r from-rose-500 to-transparent",
    laborTokens: ["orders", "misc"],
  },
  {
    key: "clamp_qc",
    label: "Clamp QC",
    shortLabel: "Clamp QC",
    accent: "bg-fuchsia-500",
    block: "bg-fuchsia-500 border-fuchsia-600",
    activeBlock: "border-l-2 border-fuchsia-600 bg-gradient-to-r from-fuchsia-500 to-transparent",
    laborTokens: ["clamp-qc", "clamp qc"],
  },
  {
    key: "kitting",
    label: "Picking",
    shortLabel: "Pick",
    accent: "bg-amber-500",
    block: "bg-amber-500 border-amber-600",
    activeBlock: "border-l-2 border-amber-600 bg-gradient-to-r from-amber-500 to-transparent",
    laborTokens: ["kitting"],
  },
  {
    key: "config",
    label: "Config",
    shortLabel: "Config",
    accent: "bg-brand-500",
    block: "bg-brand-500 border-brand-600",
    activeBlock: "border-l-2 border-brand-600 bg-gradient-to-r from-brand-500 to-transparent",
    laborTokens: ["config"],
  },
  {
    key: "wireless_check",
    label: "Wireless check",
    shortLabel: "Wireless",
    accent: "bg-cyan-600",
    block: "bg-cyan-600 border-cyan-700",
    activeBlock: "border-l-2 border-cyan-700 bg-gradient-to-r from-cyan-600 to-transparent",
    laborTokens: ["wireless-check", "wireless check"],
  },
  {
    key: "cal_test",
    label: "Cal+Test",
    shortLabel: "Cal+Test",
    accent: "bg-violet-500",
    block: "bg-violet-500 border-violet-600",
    activeBlock: "border-l-2 border-violet-600 bg-gradient-to-r from-violet-500 to-transparent",
    laborTokens: ["cal+test", "cal + test"],
  },
  {
    key: "pre_encapsulation",
    label: "Pre-Encapsulation",
    shortLabel: "Pre-Encap",
    accent: "bg-emerald-500",
    block: "bg-emerald-500 border-emerald-600",
    activeBlock: "border-l-2 border-emerald-600 bg-gradient-to-r from-emerald-500 to-transparent",
    laborTokens: ["pre-encapsulation", "pre encapsulation"],
  },
  {
    key: "encapsulation",
    label: "Encapsulation",
    shortLabel: "Encap",
    accent: "bg-green-600",
    block: "bg-green-600 border-green-700",
    activeBlock: "border-l-2 border-green-700 bg-gradient-to-r from-green-600 to-transparent",
    laborTokens: ["encapsulation", "potting", "conformal-coat", "conformal coat"],
  },
  {
    key: "final_qc",
    label: "Final QC",
    shortLabel: "Final QC",
    accent: "bg-teal-500",
    block: "bg-teal-500 border-teal-600",
    activeBlock: "border-l-2 border-teal-600 bg-gradient-to-r from-teal-500 to-transparent",
    laborTokens: ["final-qc", "final qc"],
  },
  {
    key: "pre_pack",
    label: "Pre-Pack",
    shortLabel: "Pre-Pack",
    accent: "bg-sky-400",
    block: "bg-sky-400 border-sky-500",
    activeBlock: "border-l-2 border-sky-500 bg-gradient-to-r from-sky-400 to-transparent",
    laborTokens: ["pre-pack", "pre pack"],
  },
  {
    key: "shipping_team",
    label: "Shipping Team",
    shortLabel: "Ship",
    accent: "bg-slate-500",
    block: "bg-slate-500 border-slate-600",
    activeBlock: "border-l-2 border-slate-600 bg-gradient-to-r from-slate-500 to-transparent",
    laborTokens: ["shipping-team", "shipping team"],
  },
] as const;

type ManufacturingReportingStationKey = (typeof MANUFACTURING_REPORTING_STATION_COLORS)[number]["key"];

/** Dot accents for Time tracking cards and grids — same order as {@link MANUFACTURING_REPORTING_STATION_COLORS}. */
export const MANUFACTURING_REPORTING_STATION_ACCENTS = MANUFACTURING_REPORTING_STATION_COLORS.map(
  (station) => station.accent,
);

const STATION_BY_KEY = new Map(
  MANUFACTURING_REPORTING_STATION_COLORS.map((station) => [station.key, station]),
);

const UNKNOWN_STATION_BLOCK = "bg-slate-400 border-slate-500";
const UNKNOWN_STATION_ACTIVE_BLOCK =
  "border-l-2 border-slate-500 bg-gradient-to-r from-slate-400 to-transparent";

function baseLaborLabelToken(label: string): string {
  const trimmed = label.trim();
  const dashSplit = trimmed.split(" - ")[0]?.trim() ?? trimmed;
  return dashSplit.toLowerCase().replace(/\s+/g, " ");
}

/** Map a hub labor page or mobile task label to a reporting station key. */
export function manufacturingReportingStationKeyFromLaborLabel(
  label: string,
  kind: "device" | "task" | "misc",
): string {
  if (kind === "misc") {
    return "misc";
  }
  const token = baseLaborLabelToken(label);
  const normalized = token.replace(/\s+/g, "-");
  for (const station of MANUFACTURING_REPORTING_STATION_COLORS) {
    if (
      station.laborTokens.some(
        (laborToken) => laborToken === token || laborToken === normalized || token.startsWith(laborToken),
      )
    ) {
      return station.key;
    }
  }
  return "unknown";
}

export function manufacturingReportingStationBlockClass(stationKey: string): string {
  return STATION_BY_KEY.get(stationKey as ManufacturingReportingStationKey)?.block ?? UNKNOWN_STATION_BLOCK;
}

export function manufacturingReportingStationActiveBlockClass(stationKey: string): string {
  return STATION_BY_KEY.get(stationKey as ManufacturingReportingStationKey)?.activeBlock ?? UNKNOWN_STATION_ACTIVE_BLOCK;
}

export function manufacturingReportingStationAccentClass(stationKey: string): string {
  return STATION_BY_KEY.get(stationKey as ManufacturingReportingStationKey)?.accent ?? "bg-slate-400";
}
